import React, {useMemo, useState} from 'react';
import FilterChip from '../../components/chip/FilterChip';
import Game from '../../components/game/Game';
import {useSiteConfig} from '../../data/ConfigContext';
import {competitionFilters, filterByCompetition, gameKey, groupByMonth, upcomingGames} from '../../data/games';
import pages from '../../content/pages.json';
import {sportsEventsLd} from '../../seo/meta';
import {usePageMeta} from '../../seo/usePageMeta';
import {toggleItem} from '../../utils/array';
import './schedule.css';

const Schedule = () => {
    const config = useSiteConfig();
    const [selected, setSelected] = useState<string[]>([]);

    const games = useMemo(() => upcomingGames(config?.appointments ?? []), [config]);
    // the next games as events, so search engines can show them directly in the results
    const eventsLd = useMemo(() => (games.length ? sportsEventsLd(games.slice(0, 30)) : undefined), [games]);
    usePageMeta({title: pages.schedule.title, description: pages.schedule.description, jsonLd: eventsLd});
    // the filter is generated from the appointments, so every competition with upcoming games can be selected
    const filters = useMemo(() => {
        const scheduled = new Set(games.map(game => game.bewerb_kurz));
        return competitionFilters(config?.appointments ?? []).filter(filter => scheduled.has(filter.filter_name));
    }, [config, games]);
    const months = useMemo(() => groupByMonth(filterByCompetition(games, selected)), [games, selected]);

    return (
        <div className="page">
            <h1 className="page-title">Termine</h1>

            {filters.length > 1 && (
                <div className="chip-group chip-group--scroll schedule-filter" role="group" aria-label="Nach Bewerb filtern">
                    <FilterChip label="Alle" active={!selected.length} onClick={() => setSelected([])}/>
                    {filters.map(filter => (
                        <FilterChip key={filter.filter_name} label={filter.display_name}
                                    active={selected.includes(filter.filter_name)}
                                    onClick={() => setSelected(current => toggleItem(current, filter.filter_name))}/>
                    ))}
                </div>
            )}

            {months.length > 0 && (
                <p className="schedule-legend" aria-hidden="true">
                    <span><i className="schedule-legend-swatch schedule-legend-swatch--home"/>Heimspiel</span>
                    <span><i className="schedule-legend-swatch"/>Auswärtsspiel</span>
                </p>
            )}

            {!config && (
                <div className="schedule-list" aria-hidden="true">
                    {Array.from({length: 6}, (_, i) => <div key={i} className="schedule-skeleton skeleton"/>)}
                </div>
            )}

            {months.map(month => (
                <section key={month.key} className="schedule-month">
                    <h2 className="schedule-month-title">{month.label}</h2>
                    <div className="schedule-list">
                        {month.games.map(game => <Game key={gameKey(game)} game={game} variant="row"/>)}
                    </div>
                </section>
            ))}

            {config && !months.length && <p className="empty-state">Derzeit sind keine Spiele angesetzt.</p>}
        </div>
    );
};

export default Schedule;
